import React, { useContext, useState, useMemo } from 'react'
import get from 'lodash/get'

import Button from '@react/react-spectrum/Button'
import Heading from '@react/react-spectrum/Heading'
import Select from '@react/react-spectrum/Select'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'

import ViewGrid from '@react/react-spectrum/Icon/ViewGrid'
import ViewList from '@react/react-spectrum/Icon/ViewList'
import Search from '@react/react-spectrum/Search'
import ProjectList from 'components/ProjectList'
import NewProjectDialog from './dialog/NewProjectDialog'
import Pagination from './Pagination'
import usePagination from './hooks'
import { PROJECT_TYPES } from 'config/constants'
import { RootContext } from 'root'
import { registerOpportunity } from 'services/opportunity'
import { createProject } from 'services/project'

const SORT_OPTIONS = [
  { label: 'Last updated', value: 'updated', descriptor: { propName: 'updatedAt', direction: -1 } },
  { label: 'Last created', value: 'created', descriptor: { propName: 'createdAt', direction: -1 } },
  { label: 'Title (A -> Z)', value: 'titleAZ', descriptor: { propName: 'meta.title', direction: 1 } },
  { label: 'Title (Z -> A)', value: 'titleZA', descriptor: { propName: 'meta.title', direction: -1 } },
  { label: 'Type (A -> Z)', value: 'typeAZ', descriptor: { propName: 'meta.type', direction: 1 } },
  { label: 'Type (Z -> A)', value: 'typeZA', descriptor: { propName: 'meta.type', direction: -1 } },
]

const projectTypes = Object.values(PROJECT_TYPES).map(({ label, value }) => ({ label, value }))

const projectTypeOptions = [
  { label: 'All', value: 'all', relatedTypes: Object.values(projectTypes).map((t) => t.value) },
  { label: 'Website', value: 'web', relatedTypes: ['web', 'web-modular'] },
  { label: 'Mobile', value: 'mobile', relatedTypes: ['mobile', 'mobile-v2'] },
  {
    label: 'Customer Experience',
    value: 'customer-experience',
    relatedTypes: ['customer-experience', 'customer-experience-v2'],
  },
  { label: 'Event Trigger', value: 'journey-trigger', relatedTypes: ['journey-trigger'] },
  { label: 'Server-Side Application', value: 'ssr', relatedTypes: ['ssr'] },
  { label: 'Microsites', value: 'customer-hub', relatedTypes: ['customer-hub'] },
  { label: 'Standalone Website', value: 'web-social', relatedTypes: ['web-social'] },
  { label: 'External Website', value: 'external-website', relatedTypes: ['external-website'] },
]

const getSortDescriptor = () => localStorage.getItem('projects_sortDescriptor') || SORT_OPTIONS[0].value

const saveSortDescriptor = (value) => {
  localStorage.setItem('projects_sortDescriptor', value)
}

const paginate = (projects, page, itemsPerPage) => {
  const start = (page - 1) * itemsPerPage
  return projects.slice(start, start + itemsPerPage)
}
export default function UserProjects({ projects }) {
  const { user } = useContext(RootContext)
  const [view, setView] = useState(localStorage.getItem('projectListView') || 'cards')
  const [sortDescriptorId, setSortDescriptorId] = useState(getSortDescriptor())
  const [filter, setFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  const [selectedProjectType, setSelectedProjectType] = useState(projectTypeOptions[0]?.value || '')

  const updateSortDescriptor = (value) => {
    saveSortDescriptor(value)
    setSortDescriptorId(value)
  }
  const {
    currentPage: myProjectsPage,
    itemsPerPage: myProjectsPerPage,
    setCurrentPage: setMyProjectsPage,
  } = usePagination(view)
  const {
    currentPage: sharedProjectsPage,
    itemsPerPage: sharedProjectsPerPage,
    setCurrentPage: setSharedProjectsPage,
  } = usePagination(view)

  const sortDescriptor = useMemo(
    () => SORT_OPTIONS.find((sd) => sd.value === sortDescriptorId)?.descriptor,
    [sortDescriptorId]
  )

  const filteredAndSortedProjects = useMemo(() => {
    let ownedProjects = []
    let sharedProjects = []

    let filteredProjects = projects.filter((p) => !p.isFeatured)

    filteredProjects = filteredProjects.filter((p) => {
      if (selectedProjectType === 'all') return true

      const selectedTypeObj = projectTypeOptions.find((type) => type.value === selectedProjectType)
      return selectedTypeObj.relatedTypes.includes(p.meta.type)
    })

    const sortedProjects = filteredProjects.sort((a, b) => {
      const aValue = '' + get(a, sortDescriptor?.propName)
      const bValue = '' + get(b, sortDescriptor?.propName)
      return sortDescriptor?.direction * aValue.localeCompare(bValue)
    })

    sortedProjects.forEach((project) => {
      project.owner === user.id ? ownedProjects.push(project) : sharedProjects.push(project)
    })

    if (searchQuery) {
      const filterByQuery = (projectsList) =>
        projectsList.filter((project) => project.meta.title.toLowerCase().includes(searchQuery.toLowerCase()))
      ownedProjects = filterByQuery(ownedProjects)
      sharedProjects = filterByQuery(sharedProjects)
    }

    return { ownedProjects, sharedProjects }
  }, [projects, sortDescriptor, searchQuery, selectedProjectType, user.id])

  const paginatedOwnedProjects = useMemo(
    () => paginate(filteredAndSortedProjects.ownedProjects, myProjectsPage, myProjectsPerPage),
    [filteredAndSortedProjects.ownedProjects, myProjectsPage, myProjectsPerPage]
  )

  const paginatedSharedProjects = useMemo(
    () => paginate(filteredAndSortedProjects.sharedProjects, sharedProjectsPage, sharedProjectsPerPage),
    [filteredAndSortedProjects.sharedProjects, sharedProjectsPage, sharedProjectsPerPage]
  )

  const changeView = (v) => {
    localStorage.setItem('projectListView', v)
    setView(v)
    setMyProjectsPage(1)
    setSharedProjectsPage(1)
  }

  return (
    <div className={'userProjects userProjects--' + view}>
      <div className='userProjects__viewOptions'>
        <Search aria-label='Search' value={searchQuery} onChange={setSearchQuery} />
        <section>
          <Select options={SORT_OPTIONS} value={sortDescriptorId} onSelect={updateSortDescriptor} quiet />
        </section>
        <section>
          <Select
            options={projectTypeOptions}
            value={selectedProjectType}
            onSelect={setSelectedProjectType}
            quiet
            placeholder='Select Project Type'
          />
        </section>

        <section>
          <Select
            options={[
              { label: 'All Projects', value: 'all' },
              { label: 'My Projects', value: 'owned' },
              { label: 'Shared with Me', value: 'shared' },
            ]}
            value={filter}
            onSelect={setFilter}
            quiet
          />
        </section>

        <section>
          <Button variant='tool' icon={<ViewGrid />} onClick={() => changeView('cards')} />
          <Button variant='tool' icon={<ViewList />} onClick={() => changeView('table')} />
        </section>

        <section>
          <ModalTrigger>
            <Button variant='cta' disabled={['anonymous', 'helium'].includes(user.id)}>
              New project
            </Button>
            <NewProjectDialog onProjectCreate={createProject} onDrNumberSubmit={registerOpportunity} />
          </ModalTrigger>
        </section>
      </div>

      {(filter === 'all' || filter === 'owned') && (
        <section>
          <span className='userProjects__heading'>
            <Heading variant='pageTitle'>My projects</Heading>
          </span>

          <ProjectList projects={paginatedOwnedProjects} canDelete={true} viewMode={view} />
          {filteredAndSortedProjects.ownedProjects.length / myProjectsPage > 1 && (
            <Pagination
              page={myProjectsPage}
              setPage={setMyProjectsPage}
              totalItems={filteredAndSortedProjects.ownedProjects.length}
              itemsPerPage={myProjectsPerPage}
              view={view}
            />
          )}
        </section>
      )}

      {(filter === 'all' || filter === 'shared') && filteredAndSortedProjects.sharedProjects.length > 0 && (
        <section>
          <span className='sharedProjects__heading'>
            <Heading variant='pageTitle'>Shared with me</Heading>
          </span>

          <ProjectList projects={paginatedSharedProjects} canDelete={false} viewMode={view} showOwner />
          {filteredAndSortedProjects.sharedProjects.length / sharedProjectsPerPage > 1 && (
            <Pagination
              page={sharedProjectsPage}
              setPage={setSharedProjectsPage}
              totalItems={filteredAndSortedProjects.sharedProjects.length}
              itemsPerPage={sharedProjectsPerPage}
              view={view}
            />
          )}
        </section>
      )}
    </div>
  )
}
